import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { authClient, organization } from '@/lib/auth-client'
import { AuthLayout } from '@/features/auth/components'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { generateLinks, generateMetaTags, routeSEO } from '@/lib/seo'

export const Route = createFileRoute('/accept-invitation')({
  head: () => ({
    meta: generateMetaTags(routeSEO['/accept-invitation']),
    links: generateLinks(routeSEO['/accept-invitation']),
  }),
  component: AcceptInvitationPage,
})

interface InvitationDetails {
  id: string
  email: string
  role: string
  organizationId: string
  organizationName: string
  organizationSlug: string
  inviterEmail: string
  status: string
  expiresAt: string
}

type PageStatus = 'loading' | 'authenticating' | 'ready' | 'accepting' | 'declining' | 'success' | 'declined' | 'error'

function AcceptInvitationPage() {
  const router = useRouter()
  const [status, setStatus] = useState<PageStatus>('loading')
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [error, setError] = useState<string | null>(null)

  const invitationId =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('id')
      : null

  useEffect(() => {
    async function init() {
      if (!invitationId) {
        setError('Invalid invitation link. No invitation ID found.')
        setStatus('error')
        return
      }

      setStatus('authenticating')
      const session = await authClient.getSession()

      if (!session.data) {
        const returnUrl = `/accept-invitation?id=${invitationId}`
        window.location.href = `/sign-in?redirect=${encodeURIComponent(returnUrl)}`
        return
      }

      try {
        const res = await fetch(`/api/auth/organization/get-invitation?id=${invitationId}`, {
          credentials: 'include',
        })
        if (!res.ok) {
          const body = await res.json() as { error?: { message?: string }; message?: string }
          throw new Error(body.error?.message || body.message || 'Invitation not found or expired')
        }
        const data = await res.json() as InvitationDetails
        setInvitation(data)
        setStatus('ready')
      } catch (err: any) {
        setError(err.message || 'Failed to load invitation details')
        setStatus('error')
      }
    }

    init()
  }, [invitationId])

  const handleAccept = async () => {
    if (!invitationId) return
    setStatus('accepting')
    const result = await organization.acceptInvitation({ invitationId })
    if (result.error) {
      setError(result.error.message || 'Failed to accept invitation')
      setStatus('error')
      return
    }
    setStatus('success')
    setTimeout(() => router.navigate({ to: '/dashboard/workspaces' }), 2000)
  }

  const handleDecline = async () => {
    if (!invitationId) return
    setStatus('declining')
    const result = await organization.rejectInvitation({ invitationId })
    if (result.error) {
      setError(result.error.message || 'Failed to decline invitation')
      setStatus('error')
      return
    }
    setStatus('declined')
  }

  return (
    <AuthLayout endpoint="/api/auth/organization/accept-invitation">
      {(status === 'loading' || status === 'authenticating') && (
        <div className="py-4 text-center">
          <Loader2 className="mx-auto mb-4 size-8 animate-spin text-primary" />
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {status === 'authenticating' ? 'Checking your session...' : 'Loading...'}
          </h1>
        </div>
      )}

      {status === 'ready' && invitation && (
        <div>
          <div className="mb-6 flex items-start gap-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl border border-[var(--color-border)] bg-primary/10">
              <span className="material-symbols-outlined text-2xl text-primary">group_add</span>
            </div>
            <div className="min-w-0">
              <p className="mb-1 font-mono text-caption uppercase tracking-[0.12em] text-[var(--color-text-tertiary)]">
                Invitation to join
              </p>
              <h1 className="truncate text-2xl font-bold tracking-[-0.02em] text-[var(--color-text-primary)]">
                {invitation.organizationName}
              </h1>
              <p className="truncate font-mono text-caption text-[var(--color-text-tertiary)]">
                {invitation.organizationSlug}
              </p>
            </div>
          </div>

          <dl className="mb-6 divide-y divide-[var(--color-border)] overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-primary)]/60">
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <dt className="text-body-sm text-[var(--color-text-tertiary)]">Invited by</dt>
              <dd className="truncate font-mono text-caption text-[var(--color-text-primary)]">{invitation.inviterEmail}</dd>
            </div>
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <dt className="text-body-sm text-[var(--color-text-tertiary)]">Your role</dt>
              <dd>
                <Badge variant="neutral" className="capitalize">{invitation.role}</Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <dt className="text-body-sm text-[var(--color-text-tertiary)]">Status</dt>
              <dd>
                <Badge variant="success">Pending</Badge>
              </dd>
            </div>
          </dl>

          <div className="flex gap-3">
            <Button
              variant="primary"
              className="flex-1 font-semibold tracking-tight"
              onClick={handleAccept}
            >
              Accept invitation
            </Button>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={handleDecline}
            >
              Decline
            </Button>
          </div>
        </div>
      )}

      {status === 'accepting' && (
        <div className="py-4 text-center">
          <Loader2 className="mx-auto mb-4 size-8 animate-spin text-primary" />
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Accepting invitation...</h1>
        </div>
      )}

      {status === 'declining' && (
        <div className="py-4 text-center">
          <Loader2 className="mx-auto mb-4 size-8 animate-spin text-primary" />
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Declining invitation...</h1>
        </div>
      )}

      {status === 'success' && (
        <div className="text-center">
          <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-full border border-[var(--color-success-border)] bg-[var(--color-success-bg)]">
            <span className="material-symbols-outlined text-3xl text-[var(--color-success)]">check_circle</span>
          </div>
          <h1 className="mb-1.5 text-xl font-bold tracking-[-0.02em] text-[var(--color-text-primary)]">Invitation accepted</h1>
          <p className="text-body-sm text-[var(--color-text-secondary)]">Redirecting to your workspace...</p>
        </div>
      )}

      {status === 'declined' && (
        <div className="text-center">
          <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-full border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)]">
            <span className="material-symbols-outlined text-3xl text-[var(--color-warning)]">block</span>
          </div>
          <h1 className="mb-1.5 text-xl font-bold tracking-[-0.02em] text-[var(--color-text-primary)]">Invitation declined</h1>
          <p className="mb-6 text-body-sm text-[var(--color-text-secondary)]">The organization will be notified.</p>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-primary underline-offset-4 hover:underline"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            Go to dashboard
          </a>
        </div>
      )}

      {status === 'error' && (
        <div className="text-center">
          <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-full border border-[var(--color-error-border)] bg-[var(--color-error-bg)]">
            <span className="material-symbols-outlined text-3xl text-[var(--color-error)]">error</span>
          </div>
          <h1 className="mb-1.5 text-xl font-bold tracking-[-0.02em] text-[var(--color-text-primary)]">Unable to process invitation</h1>
          <p className="mb-6 text-body-sm text-[var(--color-text-secondary)]">{error}</p>
          <a
            href={invitationId ? `/sign-in?redirect=${encodeURIComponent(`/accept-invitation?id=${invitationId}`)}` : '/dashboard'}
            className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-primary underline-offset-4 hover:underline"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            {invitationId ? 'Sign in to try again' : 'Go to dashboard'}
          </a>
        </div>
      )}
    </AuthLayout>
  )
}
